/**
 * Los cinco documentos que produce una cotización.
 *
 * En la plataforma anterior estos documentos existían completos en las plantillas
 * HTML de la app móvil, y en la web nunca se portaron: quedaron como componentes
 * vacíos que decían `Section #2`. Aquí viven en la web, que es la única interfaz
 * que va a existir.
 *
 * Cada uno es para una audiencia distinta:
 *   cotización  → el cliente
 *   materiales  → almacén y compras
 *   orden       → acta de entrega, firmada por cliente e instalador
 *   utilidades  → dirección, interno
 *   comisiones  → vendedor o distribuidor
 */

import { useState } from 'react'
import { porProducto, porCotizacion, rentabilidad, subtotal, materiales } from '../motor/calculo.js'
import { renglonesDeSalida } from '../motor/bodega.js'
import { EQUIPOS } from '../motor/catalogo.js'
import { MARCA, encabezadoMarca } from '../marca.js'
import { esOpciones, rotulo, rotuloCorto } from '../bloques.js'
import Sello from './Sello.jsx'
import { FOTO_PORTADA } from '../fotos.js'

const mx = n => '$' + Math.round(n || 0).toLocaleString('es-MX')
const pct = n => (n || 0).toFixed(1) + '%'

const DOCS = [
  ['cotizacion', 'Cotización'],
  ['materiales', 'Materiales'],
  ['orden', 'Orden de servicio'],
  ['utilidades', 'Utilidades'],
  ['comisiones', 'Comisiones']
]

/**
 * Membrete del documento. Lleva el isotipo de la marca —hexágono y espiral— porque es
 * la pieza que un cliente reconoce del sitio y de la tarjeta, y porque un entregable
 * sin marca no se distingue de una hoja de cálculo impresa.
 */
export function Encabezado({ titulo, cot, cliente, portada, credenciales = MARCA.credenciales }) {
  return (
    <>
      {/* Banda de portada solo en la propuesta: es el documento que el cliente mira, y
          es lo que separa una propuesta de una hoja de cálculo impresa. La imagen es
          ILUSTRATIVA de la marca, nunca se presenta como obra propia. */}
      {portada && <div className="banda"><img src={FOTO_PORTADA} alt="" /></div>}
      <div className="membrete">
        <img className="isotipo" src="/isotipo-soham.png" alt="" width="52" height="56" />
        <div className="grow">
          <div className="marca">{encabezadoMarca()}</div>
          <div className="descriptor">{MARCA.descriptor}</div>
        </div>
        <div className="folio">
          <span>{titulo}</span>
          <b>{cot.fecha}</b>
        </div>
      </div>
      {/* Las credenciales son publicidad comprobable (LFPC art. 32): «Dictamen firmado»
          solo se imprime cuando existe un dictamen para esta casa; el llamador lo decide. */}
      <div className="credenciales">{MARCA.fraseMadre} · {credenciales}</div>
      <div className="dirigido">
        <span className="k">Preparada para</span>
        <b>{cliente?.nombre}</b>
        {[cliente?.direccion, cliente?.telefono, cliente?.correo].filter(Boolean).length > 0 && (
          <span className="s">
            {[cliente?.direccion, cliente?.telefono, cliente?.correo].filter(Boolean).join(' · ')}
          </span>
        )}
        <span className="s">{cot.nombre}</span>
      </div>
    </>
  )
}

/** Renglones de una etapa, agrupados por habitación. */
/**
 * Los supuestos de todas las partidas de levantamiento de la cotización, juntos.
 *
 * Se recorren TODAS las habitaciones y TODAS las etapas, no sólo la que se está viendo:
 * un supuesto que condiciona la etapa 3 sigue siendo una condición del documento.
 *
 * Se deduplican porque varios rubros repiten el mismo aviso —el de que los costos del
 * taller no están calibrados, por ejemplo— y verlo cinco veces le quita peso a los cinco.
 */
function Supuestos({ cot, ctx }) {
  const vistos = new Set()
  const lista = []

  for (const h of cot.habitaciones || []) {
    for (const i of h.items || []) {
      const b = ctx.P.get(i.sku)
      for (const s of b?.supuestos || []) {
        if (vistos.has(s)) continue
        vistos.add(s)
        lista.push({ donde: h.nombre, texto: s })
      }
    }
  }

  if (!lista.length) return null

  return (
    <div className="card">
      <b>Sobre qué descansa este precio</b>
      <div className="s" style={{ marginTop: 5 }}>
        Lo siguiente se calculó con la información disponible durante el levantamiento. No
        son exclusiones: son las condiciones que, de cambiar, mueven el precio — y por eso
        se dicen aquí y no después.
      </div>
      <ul style={{ margin: '8px 0 0', paddingLeft: '1.1rem' }}>
        {lista.map((s, n) => (
          <li key={n} className="s" style={{ marginBottom: 3 }}>
            <b>{s.donde}:</b> {s.texto}
          </li>
        ))}
      </ul>
    </div>
  )
}

function DetalleEtapa({ cot, ctx, etapa, factor }) {
  return (cot.habitaciones || []).map(h => {
    const items = (h.items || []).filter(i => i['e' + etapa])
    if (!items.length) return null
    return (
      <div key={h.id}>
        <div style={{ margin: '8px 0 2px', fontWeight: 650, fontSize: 13.5 }}>{h.nombre}</div>
        {items.map((i, k) => {
          const b = ctx.P.get(i.sku) || {}
          return (
            <div key={k}>
              <div className="ln">
                <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  {b.img
                    ? <img alt="" src={b.img} style={{ width: 24, height: 24, borderRadius: 2 }} />
                    : <span className="ph vi" />}
                  {b.nombre} × {i.q}
                </span>
                {/* Una pieza sin precio capturado se imprime como «por definir», nunca como
                    $0: un cero en la propuesta se lee como un precio, y confundiría la vía
                    modular con la de proyecto en el mismo renglón. */}
                {b.porDefinir
                  ? <Sello>Por definir · vía de proyecto</Sello>
                  : <b>{mx(Math.round(subtotal(porProducto(i, ctx)) * factor))}</b>}
              </div>

              {/* QUÉ HACE LO COTIZADO, dicho al cliente (pedido de Juan, 2026-08-18):
                  cada renglón explica qué es y por qué le importa, en su lenguaje.
                  Vive en el catálogo (`explica`, editable por producto): la propuesta
                  deja de ser una lista de nombres técnicos con precio. */}
              {b.explica && (
                <div className="s" style={{ margin: '0 0 2px 31px' }}>
                  {b.explica}
                </div>
              )}

              {/* LO QUE TRAE UNA PARTIDA DE LEVANTAMIENTO, EN EL DOCUMENTO QUE FIRMA EL
                  CLIENTE. Hasta hoy salía sólo el nombre y el importe: una partida de
                  ochenta mil pesos se leía igual que una de mil, sin nada que la explicara.

                  El `resumen` es lo que la justifica —«11 circuitos en 150 m², acometida de
                  37 A»— y es exactamente lo que un cliente necesita para comparar contra
                  otro presupuesto. Va en el documento ETIQUETADO como suyo: los datos
                  reales de su casa son el argumento de venta que ningún competidor que
                  cotiza por teléfono puede imprimir.

                  `pendientes` NO se imprime: es información de operación —qué falta dar de
                  alta en el catálogo— y no le dice nada al cliente. */}
              {b.resumen?.length > 0 && (
                <div className="s" style={{ margin: '0 0 4px 31px' }}>
                  <b>De su levantamiento:</b> {b.resumen.join(' · ')}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  })
}

export default function Documentos({ cot, cliente, ctx, comparativo, equipos = EQUIPOS, herramientas = [], credenciales = MARCA.credenciales }) {
  const [doc, setDoc] = useState('cotizacion')
  const val = porCotizacion(cot, ctx)
  const r = rentabilidad(cot, ctx)
  const equipo = equipos.find(e => e.id === cot.equipo) || equipos[0]
  const activas = [1, 2, 3].filter(e => val[e].total > 0)

  const selector = (
    <>
      <div className="tabs noprint" style={{ flexWrap: 'wrap' }}>
        {DOCS.map(([k, n]) => (
          <button key={k} className={`tb ${doc === k ? 'on' : ''}`}
            style={{ flex: '1 1 30%' }} onClick={() => setDoc(k)}>{n}</button>
        ))}
      </div>
      <button className="ib pri w noprint" style={{ marginBottom: 14 }}
        onClick={() => window.print()}>Generar PDF</button>
    </>
  )

  /* ---------- Cotización para el cliente ---------- */
  if (doc === 'cotizacion') return (
    <>
      {selector}
      <div id="doc">
        <Encabezado titulo="Propuesta" cot={cot} cliente={cliente} portada credenciales={credenciales} />
        {comparativo}
        <div className="sec">Detalle por {esOpciones(cot) ? 'opción' : 'etapa'}</div>
        {activas.map(e => (
          <div className="card" key={e}>
            <div style={{ fontWeight: 500, color: `var(--b${e})`, marginBottom: 4 }}>{rotulo(cot, e)}</div>
            <DetalleEtapa cot={cot} ctx={ctx} etapa={e} factor={val.factor} />
            <div className="ln" style={{ borderTop: '1px solid var(--linea)', marginTop: 8, paddingTop: 8 }}>
              <span>Transporte e instalación</span><b>{mx(val[e].traslado + val[e].peaje)}</b>
            </div>
            <div className="ln big">
              <span style={{ color: 'var(--negro)', fontWeight: 500 }}>Total {rotuloCorto(cot, e).toLowerCase()}</span>
              <b>{mx(val[e].total)}</b>
            </div>
            {(cot.habitaciones || []).some(h => (h.items || [])
              .some(i => i['e' + e] && ctx.P.get(i.sku)?.porDefinir)) && (
              <div className="warn" style={{ marginTop: 10, marginBottom: 0 }}>
                Este importe <b>no incluye</b> las piezas marcadas «por definir»: se cotizan por
                la vía de proyecto, con levantamiento de medidas y anticipo de diseño, y su
                precio se entrega por separado.
              </div>
            )}
          </div>
        ))}
        {/* SOBRE QUÉ DESCANSA ESTE PRECIO.
            Las partidas de levantamiento traen sus supuestos —distancias no medidas,
            cobertura estimada, un muro fuera de escuadra— y hasta hoy sólo se veían en la
            pantalla interna. Eso dejaba a Juan firmando un documento que no declaraba sus
            propias condiciones, que es exactamente lo que después se discute.

            Va como sección propia y no por renglón porque son condiciones de la propuesta
            entera, y porque el cliente tiene que poder leerlas de corrido antes de firmar.
            Decirlas por escrito no debilita la cotización: la vuelve defendible. */}
        <Supuestos cot={cot} ctx={ctx} />

        {cot.notasCot && (
          <div className="card">
            <b>Notas especiales</b>
            <div className="s" style={{ marginTop: 5, whiteSpace: 'pre-wrap' }}>{cot.notasCot}</div>
          </div>
        )}
        <div className="s" style={{ lineHeight: 1.5 }}>{MARCA.condiciones}</div>
        {/* Lo que la LFPC exige por escrito en una venta a domicilio (art. 52: proveedor y
            domicilio, garantías; art. 56: plazo de revocación) y la garantía con su
            alcance y su mecanismo (art. 40, mínimo 90 días art. 77). */}
        <div className="s" style={{ lineHeight: 1.5, marginTop: 10 }}>{MARCA.garantia}</div>
        <div className="s" style={{ lineHeight: 1.5, marginTop: 10 }}>{MARCA.revocacion}</div>
        {MARCA.domicilio && (
          <div className="s" style={{ lineHeight: 1.5, marginTop: 10 }}>Proveedor: {MARCA.razonSocial || MARCA.nombre} · {MARCA.domicilio}</div>
        )}
      </div>
    </>
  )

  /* ---------- Lista de materiales ---------- */
  if (doc === 'materiales') {
    const m = materiales(cot, ctx)
    const total = m.reduce((a, x) => a + x.costo * x.q, 0)
    return (
      <>
        {selector}
        <div id="doc">
          <Encabezado titulo="Lista de materiales" cot={cot} cliente={cliente} />
          <table className="d">
            <thead><tr>
              <th>Insumo</th><th>Tipo</th><th className="n">Cant.</th>
              <th className="n">Costo unit.</th><th className="n">Importe</th>
            </tr></thead>
            <tbody>
              {m.map(x => (
                <tr key={x.sku}>
                  <td>{x.nombre}<div className="s">{x.sku}</div></td>
                  <td>{x.tipo}</td>
                  <td className="n">{Math.ceil(x.q)}</td>
                  <td className="n">{mx(x.costo)}</td>
                  <td className="n">{mx(x.costo * x.q)}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={4} style={{ fontWeight: 500, borderTop: '2px solid var(--negro)' }}>
                  Total de material a costo</td>
                <td className="n" style={{ fontWeight: 500, borderTop: '2px solid var(--negro)' }}>
                  {mx(total)}</td>
              </tr>
            </tbody>
          </table>
          <div className="s" style={{ marginTop: 10 }}>
            Consolidado de toda la cotización: el mismo insumo se suma aunque esté en habitaciones
            distintas. Documento interno, para almacén y compras.
          </div>
        </div>
      </>
    )
  }

  /* ---------- Orden de servicio ---------- */
  /* Qué llevar: nadie sale a una instalación de memoria. Los productos y el material
     los calcula el motor; las herramientas son la unión, sin duplicados, de las que
     declara cada producto de la orden. El registro de qué salió y qué volvió vive en
     la pantalla de Bodega — esto es la lista para CARGAR la camioneta. */
  const carga = doc === 'orden' ? renglonesDeSalida(cot, ctx, herramientas) : []
  const cargaMaterial = carga.filter(r => r.tipo === 'producto' || r.tipo === 'material')
  const cargaHerr = carga.filter(r => r.tipo === 'herramienta')

  if (doc === 'orden') return (
    <>
      {selector}
      <div id="doc">
        <Encabezado titulo="Orden de servicio" cot={cot} cliente={cliente} />
        <div className="card">
          <b>Declaraciones</b>
          <div className="s" style={{ marginTop: 6, lineHeight: 1.5 }}>
            El cliente declara haber recibido a su entera satisfacción los trabajos descritos,
            haber sido informado del alcance y de las condiciones de garantía, y autoriza su
            ejecución conforme a la propuesta {cot.nombre} de fecha {cot.fecha}.
            Alcance: {MARCA.alcance}.
          </div>
        </div>
        {/* La duración estimada la calcula el motor (minutos de toda la cotización /
            jornada) y el traslado ya se cobró por día — este renglón se la dice a la
            CUADRILLA y al cliente en el mismo papel: cuántos despliegues esperar. */}
        <div className="card">
          <div className="ln" style={{ margin: 0 }}>
            <span><b>Duración estimada de los trabajos</b></span>
            <b>{val.total.dias} {val.total.dias === 1 ? 'día de obra' : 'días de obra'}</b>
          </div>
        </div>
        <div className="sec">Trabajos entregados</div>
        {(cot.habitaciones || []).map(h => {
          const items = (h.items || []).filter(i => i.e1 || i.e2 || i.e3)
          if (!items.length) return null
          const fotos = items.flatMap(i => i.fotos || [])
          return (
            <div className="card" key={h.id}>
              <div style={{ fontWeight: 650, marginBottom: 5 }}>{h.nombre}</div>
              {items.map((i, k) => {
                const b = ctx.P.get(i.sku) || {}
                return (
                  <div className="ln" key={k}>
                    <span>{b.nombre || i.sku}{b.porDefinir && <Sello>Por definir</Sello>}</span>
                    <b>{i.q}</b>
                  </div>
                )
              })}
              {fotos.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {fotos.map((f, k) => (
                    <img key={k} src={f} alt=""
                      style={{ width: 58, height: 58, objectFit: 'cover', borderRadius: 2 }} />
                  ))}
                </div>
              )}
            </div>
          )
        })}
        {cargaMaterial.length > 0 && (
          <>
            <div className="sec">Material y productos a llevar</div>
            <div className="card">
              {cargaMaterial.map((r, k) => (
                <div className="ln" key={k}>
                  <span>{r.nombre}<span className="s" style={{ marginLeft: 6 }}>
                    {r.tipo === 'material' ? 'material' : 'producto'}</span></span>
                  <b>{r.sale}</b>
                </div>
              ))}
            </div>
          </>
        )}
        {cargaHerr.length > 0 && (
          <>
            <div className="sec">Herramientas a llevar</div>
            <div className="card">
              {cargaHerr.map((r, k) => (
                <div className="ln" key={k}><span>{r.nombre}</span><b>{r.sale}</b></div>
              ))}
              <div className="s" style={{ marginTop: 6 }}>
                La herramienta regresa completa a bodega: la salida y el regreso se
                registran en la pantalla de Bodega de esta cotización.
              </div>
            </div>
          </>
        )}

        {/* En la ORDEN DE SERVICIO los supuestos pesan todavía más que en la cotización:
            es el papel que lee la cuadrilla parada en la obra. Que diga «las distancias de
            canalización se supusieron» es lo que hace que alguien las mida antes de romper
            muro, en vez de descubrirlo a media instalación. */}
        <Supuestos cot={cot} ctx={ctx} />

        {cot.notasCot && (
          <div className="card">
            <b>Notas especiales</b>
            <div className="s" style={{ marginTop: 5, whiteSpace: 'pre-wrap' }}>{cot.notasCot}</div>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 26 }}>
          <div style={{ textAlign: 'center' }}>
            {cot.firmaCliente
              ? <img src={cot.firmaCliente} alt="Firma del cliente"
                  style={{ width: '100%', maxHeight: 90, objectFit: 'contain', background: '#fff' }} />
              : <div style={{ height: 90 }} />}
            <div style={{ borderTop: '1px solid var(--negro)', paddingTop: 5, fontSize: 12 }}>
              {cot.firmante || cliente?.nombre}<br /><b>Nombre y firma del cliente</b>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ height: 90 }} />
            <div style={{ borderTop: '1px solid var(--negro)', paddingTop: 5, fontSize: 12 }}>
              <br /><b>Firma del instalador</b>
            </div>
          </div>
        </div>
      </div>
    </>
  )

  /* ---------- Reportes internos: utilidades y comisiones ---------- */
  const renglones = []
  for (const h of cot.habitaciones || []) {
    for (const it of h.items || []) {
      if (!(it.e1 || it.e2 || it.e3)) continue
      const b = ctx.P.get(it.sku) || {}
      const v = porProducto(it, ctx)
      const venta = subtotal(v) * val.factor
      const costo = v.costoMaterial + v.costoMano +
        venta * ctx.f.CostoTDC + ctx.f.CostoDiaMetropolitano * v.factor
      renglones.push({
        nombre: b.nombre || it.sku, q: it.q,
        unitario: venta / (it.q || 1),
        utilidad: venta - costo,
        comision: venta * (equipo.proporcionDeComision / 100),
        insumos: v.costoMaterial
      })
    }
  }

  if (doc === 'utilidades') return (
    <>
      {selector}
      <div id="doc">
        <Encabezado titulo="Reporte de utilidades — interno" cot={cot} cliente={cliente} />
        <table className="d">
          <thead><tr>
            <th>Producto</th><th className="n">Cant.</th><th className="n">P. público unit.</th>
            <th className="n">Ut. unit.</th><th className="n">Ut. total</th>
          </tr></thead>
          <tbody>
            {renglones.map((f, k) => (
              <tr key={k}>
                <td>{f.nombre}</td><td className="n">{f.q}</td>
                <td className="n">{mx(f.unitario)}</td>
                <td className="n">{mx(f.utilidad / (f.q || 1))}</td>
                <td className="n">{mx(f.utilidad)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="card" style={{ marginTop: 12 }}>
          <div className="ln"><span>Precio de venta</span><b>{mx(val.total.total)}</b></div>
          <div className="ln">
            <span>Costo de operación <Sello>Sin calibrar</Sello></span>
            <b className="cifra-prov">{mx(r.costoOp)}</b>
          </div>
          <div className="ln"><span>Gastos de administración</span><b>{mx(r.gastosAdmin)}</b></div>
          <div className="ln"><span>Comisión de venta</span><b>{mx(r.comisionVenta)}</b></div>
          <div className="ln big">
            <span style={{ color: 'var(--negro)', fontWeight: 500 }}>UAFIR</span>
            <b className="cifra-prov">{mx(r.uafir)} · {pct(r.uafirPct)}</b>
          </div>
          {/* Este reporte se le enseña a dirección y a socios. Sin esta nota, un número
              provisional se lee como medido. */}
          <div className="warn" style={{ marginTop: 12, marginBottom: 0 }}>
            El gasto fijo diario ({mx(ctx.f.CostoDiaMetropolitano)} al día) y el factor de
            gasto de cada producto <b>todavía no están calibrados</b> con la operación real.
            El costo de operación y el UAFIR que aparecen arriba dependen de ellos y pueden
            estar corridos en cualquier dirección.
          </div>
        </div>
      </div>
    </>
  )

  return (
    <>
      {selector}
      <div id="doc">
        <Encabezado titulo={`Reporte de comisiones — ${equipo.nombre}`} cot={cot} cliente={cliente} />
        <table className="d">
          <thead><tr>
            <th>Producto</th><th className="n">Cant.</th><th className="n">P. público unit.</th>
            <th className="n">Consumibles e insumos</th>
            <th className="n">Com. unit.</th><th className="n">Com. total</th>
          </tr></thead>
          <tbody>
            {renglones.map((f, k) => (
              <tr key={k}>
                <td>{f.nombre}</td><td className="n">{f.q}</td>
                <td className="n">{mx(f.unitario)}</td>
                <td className="n">{mx(f.insumos)}</td>
                <td className="n">{mx(f.comision / (f.q || 1))}</td>
                <td className="n">{mx(f.comision)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={5} style={{ fontWeight: 500, borderTop: '2px solid var(--negro)' }}>
                Comisión total</td>
              <td className="n" style={{ fontWeight: 500, borderTop: '2px solid var(--negro)' }}>
                {mx(renglones.reduce((a, f) => a + f.comision, 0))}</td>
            </tr>
          </tbody>
        </table>
        <div className="s" style={{ marginTop: 10 }}>
          Canal {equipo.nombre} al {equipo.proporcionDeComision}%
          {equipo.distribuidor ? ', esquema de distribuidor' : ''}.
        </div>
      </div>
    </>
  )
}
